import { ReactNode } from "react";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { TabItem } from "components/ui/tabs/Tabs";
import { DocumentDetails as Document } from "lib/hooks/api/documents/useDocument";
import DocumentAiSummary from "./DocumentAiSummary";
import DocumentDetails from "./DocumentDetails";
import DocumentFindLeads from "./DocumentFindLeads";
import DocumentSearch from "./DocumentSearch";
import DocumentSearchTabs from "./DocumentSearchTabs";
import { DocumentViewerFilters } from "./FilterForm";

const TOOLBAR_WIDTH = 440;

export const TAB_KEYS = {
  overview: "overview",
  search: "search",
  info: "info",
  findLeads: "findLeads",
} as const;
export type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

export type TabLabel = { key: TabKey; label: string; disabled?: boolean };

export const TAB_LABELS: TabLabel[] = [
  { key: TAB_KEYS.overview, label: "Overview" },
  { key: TAB_KEYS.search, label: "Search" },
];

export type TabPanels = Partial<Record<TabKey, ReactNode>>;

export function createTabPanels(
  documentId: string,
  isLoading: boolean,
  filters: DocumentViewerFilters,
  data?: Document,
  aiEnabled: boolean = false,
  documentAiSearchEnabled?: boolean,
  refetchDocument: () => void = () => {},
  isInFallbackMode: boolean = false,
): TabPanels {
  const panels: TabPanels = {
    [TAB_KEYS.overview]: (
      <>
        <DocumentDetails
          buyers={data?.buyers ?? []}
          type={data?.category ?? ""}
          publishDate={data?.publishedAt ?? ""}
          sourceUrls={data?.sourceUrlLinks ?? []}
          alternativeSource={data?.alternativeSource ?? ""}
          isLoading={isLoading}
          isInFallbackMode={isInFallbackMode}
        />
        {aiEnabled && (
          <AiSummaryWrapper>
            <DocumentAiSummary
              key={documentId}
              documentId={documentId}
              documentTitle={data?.title ?? "Unknown Document"}
              isDocumentLoading={isLoading}
            />
          </AiSummaryWrapper>
        )}
        {aiEnabled && (
          <DocumentFindLeads
            documentId={documentId}
            hasDocumentLeads={(data?.leads?.length ?? 0) > 0}
            refetchDocument={refetchDocument}
          />
        )}
      </>
    ),
    [TAB_KEYS.search]: (
      <DocumentSearchTabs
        documentId={documentId}
        documentName={data?.title ?? "Unknown Document"}
        defaultFilters={filters}
        isDocumentAiSearchEnabled={!!documentAiSearchEnabled}
      />
    ),
  };

  return panels;
}

export function createTabItems(
  documentId: string,
  isLoading: boolean,
  isSearchDisabled: boolean,
  filters: DocumentViewerFilters,
  data?: Document,
  isInFallbackMode: boolean = false,
): TabItem[] {
  return [
    {
      key: TAB_KEYS.info,
      label: "Details",
      icon: "infoCircle",
      iconProps: { color: "sysTextDefault" },
      children: (
        <DocumentDetails
          buyers={data?.buyers ?? []}
          type={data?.category ?? ""}
          publishDate={data?.publishedAt ?? ""}
          sourceUrls={data?.sourceUrlLinks ?? []}
          alternativeSource={data?.alternativeSource ?? ""}
          isLoading={isLoading}
          isInFallbackMode={isInFallbackMode}
        />
      ),
    },
    {
      key: TAB_KEYS.search,
      label: "Search",
      icon: "searchRefraction",
      iconProps: { color: "sysTextDefault" },
      disabled: isSearchDisabled,
      children: <DocumentSearch defaultFilters={filters} documentId={documentId} />,
    },
  ];
}

export const tooltips = {
  emptyState:
    "Sorry, it looks like this isn't available yet. We're working hard to populate our documents data.",
  alternativeSource:
    "Sorry, we've collected this data from an alternative source, so there's no link available.",
};

const AiSummaryWrapper = styled(Flex)({
  flexDirection: "column",
  flexShrink: 0,
  padding: 24,
});

export const ToolbarContainerNew = styled(Flex)({
  width: TOOLBAR_WIDTH,
  flexShrink: 0,
  overflow: "auto",
  flexDirection: "column",
});

export const ToolbarContainerLegacy = styled(Flex)(({ theme }) => ({
  width: TOOLBAR_WIDTH,
  flexShrink: 0,
  overflow: "hidden",
  flexDirection: "column",
  // inactive icon
  '[role="tab"] svg': { color: theme.colors.sysTextDefault },
  // active icon — matches active text color
  '[role="tab"][data-state="active"] svg': { color: theme.colors.sysPrimaryDefault },
}));
